<template>
  <PageWrapper
    v-if="canView"
    :active-page="PAGE_PUBLICATION_COLLABORATORS"
    :active-section="SECTION_PUBLICATIONS"
    :breadcrumbs="breadcrumbs"
    page-name="Team"
    title="Team"
    align="center"
    width="full"
  >
    <iColumn>
      <iRow v-if="canInviteCollaborators">
        <iColumn gap="large">
          <iRow align="left" vertical-align="middle" width="fill">
            <iColumn width="hug">
              <iHeading font-size="extraLarge">
                Advertise to Find Help
              </iHeading>
            </iColumn>
            <iColumn width="hug">
              <iButton
                size="small"
                variant="secondary"
                @click="showAdvertisementModal"
              >
                Create new ad
              </iButton>
            </iColumn>
          </iRow>
          <iRow>
            <SmartFeed
              :pinia-store="useMyPublicationCollaborationAdFeedStore"
              default-entity-layout="cardSmall"
              default-smart-feed-layout="carousel"
            >
              <template #empty-state>
                <iRow vertical-align="middle" class="empty-state" gap="small">
                  <iText variant="subtle">
                    You have no ads at this time, <iLink @click="showAdvertisementModal">
                      create one now
                    </iLink> to start receiving applications
                  </iText>
                </iRow>
              </template>
            </SmartFeed>
          </iRow>
        </iColumn>
      </iRow>
      <iRow style="margin-top: 8px">
        <iColumn gap="small">
          <iRow align="left" vertical-align="middle" width="fill">
            <iColumn width="hug">
              <iHeading font-size="extraLarge">
                Applicants
              </iHeading>
            </iColumn>
          </iRow>
          <iRow
            v-if="canInviteCollaborators"
            vertical-align="middle"
            gap="small"
            width="fill"
          >
            <iColumn width="hug">
              <iText font-size="small">
                Enable Applications
              </iText>
            </iColumn>
            <iColumn width="hug">
              <iToggleSwitch
                size="small"
                :model-value="isApplicationsEnabled"
                :true-value="true"
                :false-value="false"
                @update:model-value="toggleApplications"
              />
            </iColumn>
            <iColumn
              v-tooltip="{
                text: 'When applications are enabled, your publication\'s profile in the creator community will invite people to apply to join your publication',
                position: 'bottom',
              }"
              width="hug"
            >
              <iIcon icon="info-circle" />
            </iColumn>
          </iRow>
          <iRow>
            <SmartFeedV2
              :source="useMyPublicationCollaboratorsApplicantFeedStore"
              default-feed-layout="table"
              :show-filters="false"
            >
              <template #empty-state>
                <iRow vertical-align="middle" class="empty-state" gap="small">
                  <iText variant="subtle">
                    You have no applicants to review at this time.
                  </iText>
                </iRow>
              </template>
            </SmartFeedV2>
          </iRow>
        </iColumn>
      </iRow>
      <iRow style="margin-top:12px">
        <iColumn gap="large">
          <iRow align="left" vertical-align="middle" width="fill">
            <iColumn width="hug">
              <iHeading font-size="extraLarge">
                Invites
              </iHeading>
            </iColumn>
            <iColumn width="hug">
              <iButton
                size="small"
                variant="secondary"
                :disabled="!canInviteCollaborators"
                @click="openInviteModal"
              >
                Send an Invite
              </iButton>
            </iColumn>
          </iRow>
          <iRow>
            <SmartFeedV2
              :source="useMyPublicationCollaboratorsInviteeFeedStore"
              default-feed-layout="table"
              :show-filters="false"
            >
              <template #empty-state>
                <iRow vertical-align="middle" class="empty-state" gap="small">
                  <iText variant="subtle">
                    You have no invites at this time
                  </iText>
                </iRow>
              </template>
            </SmartFeedV2>
          </iRow>
        </iColumn>
      </iRow>
      <iRow style="margin-top: 12px">
        <iColumn gap="large">
          <iRow>
            <iColumn gap="none">
              <iHeading font-size="extraLarge">
                Collaborators
              </iHeading>
            </iColumn>
          </iRow>
          <iRow>
            <SmartFeedV2
              :source="useMyPublicationCollaboratorsCollaboratorFeedStore"
              default-feed-layout="table"
              :show-filters="false"
            >
              <template #empty-state>
                <iRow vertical-align="middle" class="empty-state" gap="small">
                  <iText variant="subtle">
                    You have no collaborators at this time
                  </iText>
                </iRow>
              </template>
            </SmartFeedV2>
          </iRow>
        </iColumn>
      </iRow>
    </iColumn>
  </PageWrapper>
  <iText v-else>
    You don't have access to view this page.
  </iText>
  <iModal
    :visible="isAdvertisementModalVisible"
    :max-width="600"
    title="Create Advertisement"
    @close="closeAdvertisementModal"
    @click:primary="createAdvertisement"
    @click:secondary="closeAdvertisementModal"
  >
    <template #body>
      <iColumn gap="large">
        <iParagraph font-size="small">
          Ads for collaborators will appear in a special section of the
          creator community, they will also appear on your publication's
          profile. Ads allow you to explain exactly what you are looking
          for and invites people to apply for specific roles.
        </iParagraph>
        <iSelect
          v-model="advertiseRole"
          :items="AD_ROLE_OPTIONS"
          label="Role"
          width="fill"
          value-field="value"
        />
        <iTextArea
          v-model="advertiseDescription"
          label="Description"
          placeholder="Enter role description"
          :rows="5"
          width="fill"
        />
      </iColumn>
    </template>
  </iModal>
  <InviteToCollaborateOffPlatform :org-id="getOrgData.org_id" :publication-id="publicationId" />
</template>

<script>
import PageWrapper from "@/components/PageWrapper.vue";
import { AD_ROLE_OPTIONS } from "@/constants/collaborator-constants";
import {
  PAGE_PUBLICATION_COLLABORATORS,
  SECTION_PUBLICATIONS,
} from "@/constants/nav-constants";
import { useOrganizationStore } from "@/stores/organization-store";
import { mapActions, mapState } from "pinia";
import {
  useMyPublicationCollaborationAdFeedStore,
} from "SmartFeed/stores/feed/my-publication-collaboration-ad-feed-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import SmartFeedV2 from "SmartFeed/components/SmartFeedV2.vue";
import {
  useMyPublicationCollaboratorsFeedStore,
} from "@/smart-feed/stores/feed/my-publication-collaborators-feed-store";
import SmartFeed from "SmartFeed/components/SmartFeed.vue";
import {
  useMyPublicationCollaboratorsApplicantFeedStore,
} from "@/smart-feed/stores/feed/my-publication-collaborators-applicant-feed-store";
import {
  useMyPublicationCollaboratorsCollaboratorFeedStore,
} from "@/smart-feed/stores/feed/my-publication-collaborators-collaborator-feed-store";
import {
  useMyPublicationCollaboratorsInviteeFeedStore,
} from "@/smart-feed/stores/feed/my-publication-collaborators-invitee-feed-store";
import InviteToCollaborateOffPlatform from "@/components/publication/InviteToCollaborateOffPlatform.vue";

export default {
  name: "PublicationCollaborators",
  components: {
    InviteToCollaborateOffPlatform,
    SmartFeed,
    SmartFeedV2,
    PageWrapper,
  },
  props: {
    publicationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      advertiseRole: "",
      advertiseDescription: "",
      AD_ROLE_OPTIONS,
      SECTION_PUBLICATIONS,
      PAGE_PUBLICATION_COLLABORATORS,
    };
  },
  computed: {
    ...mapState(useOrganizationStore, ["getOrgData"]),
    ...mapState(useCreatorPublicationStore, [
      "publication",
      "isAdvertisementModalVisible",
      "isCreatingAdvertisement",
    ]),
    ...mapState(useCollaboratorStore, [
      "canView",
      "canInviteCollaborators",

    ]),
    isApplicationsEnabled() {
      return this.publication.enable_applications === "enabled";
    },
    breadcrumbs() {
      return [
        { url: "/", label: "Home" },
        { url: `/publication/org/${this.orgId}`, label: "My Publications" },
        {
          url: `/publication/${this.publicationId}`,
          label: this.publication.name,
        },
      ];
    },
  },
  mounted() {
    if (!this.canView) {
      const baseUrl = window.location.origin;
      const redirectUrl = `${baseUrl}/publication/${this.publicationId}`;

      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 2000);
    }
  },
  methods: {
    useMyPublicationCollaboratorsFeedStore,
    useMyPublicationCollaboratorsApplicantFeedStore,
    useMyPublicationCollaboratorsCollaboratorFeedStore,
    useMyPublicationCollaboratorsInviteeFeedStore,
    useMyPublicationCollaborationAdFeedStore,
    ...mapActions(useCreatorPublicationStore, [
      "enableApplications",
      "disableApplications",
      "createPublicationCollaboratorAd",
      "showAdvertisementModal",
      "closeAdvertisementModal",
    ]),
    ...mapActions(useCollaboratorStore, ["openInviteModal"]),
    async createAdvertisement() {
      if (!this.advertiseRole || !this.advertiseDescription) {
        return;
      }

      await this.createPublicationCollaboratorAd(
        this.publicationId,
        this.advertiseRole.value,
        this.advertiseDescription,
      );
      this.advertiseRole = "";
      this.advertiseDescription = "";
    },
    toggleApplications(value) {
      if (value) {
        this.enableApplications(this.publicationId);
      } else {
        this.disableApplications(this.publicationId);
      }
    },
  },
};
</script>
<!-- eslint-disable-next-line vue-scoped-css/require-scoped, vue-scoped-css/enforce-style-type -->
<style lang="scss">
.empty-state {
  padding: 0 !important;
}
</style>
